import * as React from 'react'
import type { PasswordStrength } from 'types/business'
import PasswordStrengthIndicator from 'components/common/password-strength-indicator'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import TextField from 'components/ui/text-field'
type Props = {
  oldPassword: string
  newPassword: string
  passwordConfirmation: string
  passwordChangeInProgress?: boolean
  onSubmit: () => unknown
  onOldPasswordChange: any
  onNewPasswordChange: any
  onPasswordConfirmationChange: any
  passwordStrength: PasswordStrength
  errors: Record<string, React.ReactNode | null | undefined>
}

/**
 * Password change form for settings page with current password verification
 * Includes current password, new password, confirmation, and forgot password link
 * @param oldPassword - Current password value
 * @param newPassword - New password value
 * @param passwordConfirmation - Password confirmation value
 * @param passwordChangeInProgress - Whether password change is in progress
 * @param onSubmit - Callback when form is submitted
 * @param onOldPasswordChange - Callback when current password input changes
 * @param onNewPasswordChange - Callback when new password input changes
 * @param onPasswordConfirmationChange - Callback when confirmation input changes
 * @param passwordStrength - Business password strength requirements
 * @param errors - Error messages for form validation
 */
const ChangePasswordSettingsForm = (props: Props): React.ReactNode => {
  const { errors } = props
  const submitEnabled =
    props.oldPassword &&
    props.newPassword &&
    props.passwordConfirmation &&
    !errors['re-enter-new-password'] &&
    !errors['new-password'] &&
    !errors['current-password']
  return (
    <>
      <Row columnsSize={[12]} marginBottom="small">
        <div className="ChangePasswordSettingsForm_InputContainer">
          <TextField
            type="password"
            label="Current password"
            autoComplete="current-password"
            compact={false}
            value={props.oldPassword}
            onChange={props.onOldPasswordChange}
            invalid={!!errors['current-password']}
            error={errors['current-password']}
          />
          <div className="ChangePasswordSettingsForm_ButtonContainer">
            <Button
              appearance="link"
              caption="Forgot Password?"
              link="/forgot-password?from=settings"
            />
          </div>
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            type="password"
            label="Choose a new password"
            autoComplete="new-password"
            compact={false}
            value={props.newPassword}
            onChange={props.onNewPasswordChange}
            invalid={!!errors['new-password']}
            error={errors['new-password']}
          />
          <PasswordStrengthIndicator
            password={props.newPassword}
            passwordStrength={props.passwordStrength}
            error={errors['weak-password']}
          />
        </div>
      </Row>
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            type="password"
            label="Re-enter password"
            autoComplete="new-password"
            compact={false}
            value={props.passwordConfirmation}
            onChange={props.onPasswordConfirmationChange}
            invalid={!!errors['re-enter-new-password']}
            error={errors['re-enter-new-password']}
          />
        </div>
      </Row>
      <Button
        disabled={!submitEnabled}
        appearance="primary"
        caption="Change password"
        onClick={props.onSubmit}
        progressCaption="Changing Password"
        progressIndicator={props.passwordChangeInProgress}
      />
    </>
  )
}

export default ChangePasswordSettingsForm
